import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { TranslateService } from '@ngx-translate/core';
import { parse, stringify } from 'qs';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { appConfig } from '~/app.config';
import { SearchModeEnum } from '~/attendee/components/pages/home/filters/enum/search-mode.enum';
import { DateRangeInterface } from '~/attendee/components/shared/date-range-input/date-range-input.component';
import { isRangeEvent } from '~/attendee/helpers/result-page/is-range-filter-payload.helper';
import { UpdateFilterPayloadInterface } from '~/attendee/interfaces/update-filter-payload.interface';
import {
  ResultPageFilters,
  ResultPageFilterService,
} from '~/attendee/service/result-page-filter.service';
import { SportsService } from '~/core/services/sports/sports.service';
import { TabBarLinkInterface } from '~/component/router-tab-bar/interfaces/tab-link.interface';
import { MatchMediaService } from '~/core/services/match-media/match-media.service';
import { ParamethorService } from '~/paramethor/paramethor.service';
import { ParamethorNameEnum } from '~/paramethor/names.enum';

@Component({
  selector: 'app-attendee-result-page',
  templateUrl: './result-page.component.html',
})
export class ResultPageComponent implements OnInit, OnDestroy {
  public readonly searchModeEnum = SearchModeEnum;
  public readonly startDate: string;

  public searchTherm: string;
  public dateRange: DateRangeInterface;
  public defaultFilters: ResultPageFilters;
  public isTabletDevice: boolean;
  public queryParamParsed = false;
  public sportsGroup: number;
  public subscriptions: Subscription = new Subscription();
  public tabBarlinks: TabBarLinkInterface[] = [];

  constructor(
    private readonly translateService: TranslateService,
    private readonly localizeRouterService: LocalizeRouterService,
    private readonly paramethorService: ParamethorService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly resultPageFilterService: ResultPageFilterService,
    private readonly router: Router,
    private readonly matchMediaService: MatchMediaService,
    private readonly sportsService: SportsService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
  public ngOnInit(): void {
    this.subscriptions.add(
      this.activatedRoute.queryParams
        .pipe(
          map((params) => {
            this.resultPageFilterService.filters$.next(
              this.parseQueryParams(params)
            );

            if (!params.sportGroups) {
              this.searchTherm = '';
            } else {
              this.sportsService
                .getSportsGroup(params.sportGroups)
                .subscribe((group) => {
                  this.searchTherm = group.label;
                });
            }
          })
        )
        .subscribe()
    );

    this.subscriptions.add(
      this.matchMediaService.isTabletDevice().subscribe((isTabletDevice) => {
        this.isTabletDevice = isTabletDevice;
      })
    );

    this.setFiltersValuesFromDefaultFilters();
    this.filterTabs();
  }

  private filterTabs(): void {
    this.tabBarlinks = [
      {
        name: 'activities',
        enum: ParamethorNameEnum.AttendeeResultsActivitiesTab,
      },
      {
        name: 'external_activities',
        enum: ParamethorNameEnum.AttendeeResultsExternalActivitiesTab,
      },
      {
        name: 'live_activities',
        enum: ParamethorNameEnum.AttendeeResultsLiveActivitiesTab,
      },
    ]
      .filter((link) => this.paramethorService.isTrue(link.enum))
      .map((link) => ({
        name: this.translateService.instant(
          `attendee.result_page.tab_bar.${link.name}`
        ),
        path: this.localizeRouterService
          .translateRoute(`attendee.${link.name.replace('_', '-')}`)
          .toString(),
      }));
  }

  private parseQueryParams(params: Params): ResultPageFilters {
    const parsedParams = parse(stringify(params, { arrayFormat: 'brackets' })); // needed to convert field[xx]=xx in url to an object in js
    this.defaultFilters = parsedParams;
    this.defaultFilters.radius = parsedParams.radius
      ? parseInt(parsedParams.radius as string, 10) // needed to convert string radius to number
      : +appConfig.filters.defaultDistance;
    this.defaultFilters.page = +parsedParams.page;

    this.queryParamParsed = true;
    this.setFiltersValuesFromDefaultFilters();

    return parsedParams;
  }

  private setFiltersValuesFromDefaultFilters(): void {
    const { startDate, endDate, sportGroups } = this.defaultFilters;

    this.dateRange = {
      from: startDate ? new Date(startDate) : undefined,
      to: endDate ? new Date(endDate) : undefined,
    };

    this.sportsGroup = Array.isArray(sportGroups)
      ? sportGroups[0]
      : sportGroups;
  }

  // update filters with emited value from search filters
  public updateFilters(updatePayloads: UpdateFilterPayloadInterface[]): void {
    const filters = {
      page: 1,
    };

    updatePayloads.forEach((updatePayload) => {
      // build a range query param in url if range event
      // undefined parameters will be removed
      if (isRangeEvent(updatePayload)) {
        filters[`${updatePayload.field}[gt]`] = updatePayload.value?.gt;
        filters[`${updatePayload.field}[gte]`] = updatePayload.value?.gte;
        filters[`${updatePayload.field}[lt]`] = updatePayload.value?.lt;
        filters[`${updatePayload.field}[lte]`] = updatePayload.value?.lte;
        return;
      }

      // default type = "SINGLE", just set field=value in url
      filters[updatePayload.field] = updatePayload.value;
    });

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        ...filters,
      },
      queryParamsHandling: 'merge', // keep other params
    });
  }
}
